import BuildIcon from '@mui/icons-material/Build';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SaveIcon from '@mui/icons-material/Save';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Input,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import getSymbolFromCurrency from 'currency-symbol-map';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { mutate as globalMutate } from 'swr';
import useConfirmSavDraft from '../../../../../../hooks/orders/useConfirmSavDraft';
import useFinalizeSavDecomposition from '../../../../../../hooks/orders/useFinalizeSavDecomposition';
import useCreateOrUpdateSavOrder from '../../../../../../hooks/orders/useCreateOrUpdateSavOrder';
import useDeleteSavDraft from '../../../../../../hooks/orders/useDeleteSavDraft';
import useFetchOrCreateBlankSavDraft from '../../../../../../hooks/orders/useFetchOrCreateBlankSavDraft';
// import useUpdateSAVDraftOrderPicker from '../../../../../../hooks/orders/useUpdateSAVDraftOrderPicker';
import { setSnackBar } from '../../../../../../states/app/staticSlice.js';
import CustomerInfos from '../Retractions/CustomerInfos';
import { resetFormAndErrors } from '../utils';
import {
  aggregateSavOrderMessages,
  buildSavOrderMessages,
} from './buildSavOrderMessages';
import SetAddress from './SavAddress.jsx';
import SavConfirmModal from './SavConfirmModal.jsx';
import SavDateTime from './SavDateTime.jsx';
import SavDecompositionCheck from './SavDecompositionCheck.jsx';
import SavDraftDeleteModal from './SavDraftDeleteModal.jsx';
import SavLinesForm from './SavLinesForm.jsx';
import { SavOrderHeader } from './SavOrderHeader.jsx';
import { getSavErrorLabels, validateSavFields } from './ValidateSavFields';

const SavOrderForm = ({
  handleShowComponent,
  orderDetails,
  delayOverDue = false,
  user,
  activeSavDraftId,
  mutateTicket,
}) => {
  const {
    data: savDraft,
    isLoading: savDraftLoading,
    isValidating: savDraftValidating,
    mutate: savDraftMutate,
  } = useFetchOrCreateBlankSavDraft({
    customerOrder: orderDetails.reference,
    savDraftId: activeSavDraftId,
    ticketRef: orderDetails?.ticket?.reference,
    createdBy: user?.email,
  });

  const dispatch = useDispatch();
  const { deleteDraft } = useDeleteSavDraft();
  const { createOrUpdateSavOrder } = useCreateOrUpdateSavOrder();
  // const { updateSAVDraftOrderPicker } = useUpdateSAVDraftOrderPicker();
  const { confirmSavDraft } = useConfirmSavDraft();
  const { finalizeSavDecomposition } = useFinalizeSavDecomposition();
  const finalizingRef = useRef(false);
  const savOrderReasons = useSelector(
    (state) => state?.static?.savOrderReasons
  );
  const [isUsingShippingAddress, setIsUsingShippingAddress] = useState(true);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  // État local pour la valeur d'affichage du montant (pour un feedback immédiat)
  const [displayAmount, setDisplayAmount] = useState('');
  const debounceRef = useRef(null);
  const decompositionRef = useRef(null);
  const stockRefreshDoneRef = useRef(false);
  const [hasCheckedDecomposition, setHasCheckedDecomposition] = useState(false);
  const [decompositionCheckRequiresAction, setDecompositionCheckRequiresAction] =
    useState(false);
  // Une décomposition (création) ou une réservation a été appliquée : la SAV est passée en
  // "attente de décomposition" côté BO, le flux est terminal et "Confirmer" doit être bloqué.
  const [decompositionApplied, setDecompositionApplied] = useState(false);
  // const [orderPickerLoading, setOrderPickerLoading] = useState(false)

  const addressFormat = (addressName) => {
    return {
      address1: orderDetails?.addresses[addressName].line1,
      address2: orderDetails?.addresses[addressName].line2,
      postalCode: orderDetails?.addresses[addressName].postalCode,
      city: orderDetails?.addresses[addressName].city,
      country: orderDetails?.addresses[addressName].country,
    };
  };

  const [formData, setFormData] = useState({
    isDraftSet: false,
    activeSavDraftId: null,
    savDraftReference: '',
    customerOrder: orderDetails.reference,
    ticket: orderDetails?.ticket?.reference,
    amountTtcNet: 100,
    lines: [],
    shippingAddress: {},
    billingAddress: {},
    isPayable: delayOverDue ? true : false,
    paymentMethod: orderDetails?.paymentMethod,
    createdBy: user?.email,
    reason: '',
    currency: orderDetails?.currency.code,
    isSet: false,
    lastName: orderDetails?.contact?.lastName,
    firstName: orderDetails?.contact?.firstName,
    phone:
      orderDetails?.contact?.internationalPhone || orderDetails?.contact?.phone,
    comment: '',
    orderPicker: '',
    desiredShippingDate: null,
    updatedAt: '',
  });

  const [formError, setFormError] = useState({
    savDraftReference: '',
    isPayable: '',
    customerOrder: '',
    lines: '',
    reason: '',
    comment: '',
    orderPicker: '',
    desiredShippingDate: '',
    amountTtcNet: '',
    shippingAddress: {
      address1: '',
      address2: '',
      postalCode: '',
      city: '',
      country: '',
    },
    billingAddress: {
      address1: '',
      address2: '',
      postalCode: '',
      city: '',
      country: '',
    },
  });

  useEffect(() => {
    setHasCheckedDecomposition(false);
    setDecompositionCheckRequiresAction(false);
    setDecompositionApplied(false);
  }, [formData.lines, formData.savOrder, formData.createdBy]);

  const hasDecompositionFlow = useMemo(
    () =>
      formData.lines.some(
        (line) => line.isValid !== false && typeof line.isAllocated === 'boolean'
      ),
    [formData.lines]
  );

  const isConfirmLockedByDecomposition =
    hasCheckedDecomposition && decompositionCheckRequiresAction;

  const handleSwitchChange = (value) => {
    if (value) {
      handleFormChange({
        key: 'billingAddress',
        value: formData.shippingAddress,
      });
    }
    setIsUsingShippingAddress(value);
  };

  const handleFormChange = ({ key, value }) => {
    if (key === 'isPayable') {
      setFormData((prevData) => ({
        ...prevData,
        isPayable: value,
        amountTtcNet: value ? 0 : 100,
      }));
    }

    setFormData((prevData) => {
      // Gestion des clés imbriquées (ex: "shippingAddress.address1")
      if (key.includes('.')) {
        const [parent, child] = key.split('.');
        return {
          ...prevData,
          [parent]: {
            ...prevData[parent],
            [child]: value,
          },
        };
      }
      // Gestion des clés simples
      return {
        ...prevData,
        [key]: value,
      };
    });
  };

  const handleOpenConfirm = () => {
    if (isConfirmLockedByDecomposition) {
      dispatch(
        setSnackBar({
          open: true,
          message:
            'Après une vérification de décomposition, utilisez "Créer la décomposition et sauvegarder".',
          color: 'info',
        })
      );
      return;
    }

    // Les erreurs d'adresse ne sont rendues que lorsque le toggle "Identique à
    // la commande" est désactivé (SavAddress.jsx), et le bloc facturation n'est
    // même pas monté tant que "identique à la livraison" est actif. Sans
    // snackbar ici, un échec de validation sur ces champs rendait le bouton
    // "Confirmer" totalement muet. On nomme donc les champs fautifs.
    let validationErrors = null;
    const captureErrors = (errors) => {
      validationErrors = errors;
      setFormError(errors);
    };

    if (validateSavFields(formData, captureErrors)) {
      setIsConfirmOpen(true);
      return;
    }

    const invalidFields = getSavErrorLabels(validationErrors);
    dispatch(
      setSnackBar({
        open: true,
        message: invalidFields.length
          ? `Erreur de saisie : ${invalidFields.join(', ')}.`
          : 'Erreur de saisie.',
        color: 'error',
      })
    );
  };

  // Analyse la réponse backend de createOrUpdateSavOrder et affiche le
  // snackbar adapté. Retourne `true` si une erreur bloquante (4xx/5xx) a été
  // rencontrée, afin que les appelants interrompent la chaîne (pas de faux
  // succès / confirm). La construction des messages est extraite dans
  // buildSavOrderMessages (pure, testable sans les hooks SWR ni Redux) : voir
  // ce module pour la logique de repli sur le motif métier backend.
  //
  // Retour CodeRabbit (2e tour), point 2 : `setSnackBar` (staticSlice) ne
  // stocke qu'un seul objet et Header.jsx n'affiche qu'un seul <Snackbar> —
  // dispatcher un setSnackBar par message dans le même tick faisait
  // disparaître tous les messages sauf le dernier (ex: le motif métier 4xx
  // écrasé par l'avertissement "référence produit invalide" poussé après).
  // aggregateSavOrderMessages fusionne la liste en un seul { message, color }
  // avant l'unique dispatch, sans toucher au reducer ni à Header.jsx (état
  // Redux partagé, hors périmètre de cette MR).
  const handleErrors = (data) => {
    const { messages, hasBlockingError } = buildSavOrderMessages(data);
    const aggregatedMessage = aggregateSavOrderMessages(messages);
    if (aggregatedMessage) {
      dispatch(setSnackBar({ open: true, ...aggregatedMessage }));
    }
    return hasBlockingError;
  };

  // Revalide la liste des SAV rattachées à la commande. Cette clé SWR est
  // partagée par le panneau "Gérer les SAVs" (ManageSav) et le fil d'activité
  // (TicketActivity) ; sans cette revalidation explicite, la SAV nouvellement
  // créée n'apparaît qu'après un refocus/poll de la fenêtre.
  const revalidateSavOrders = useCallback(() => {
    if (orderDetails?._id) {
      globalMutate(`/orders/${orderDetails._id}/savOrders`);
    }
  }, [orderDetails?._id]);

  // Toutes les lignes ont été résolues via réservation/décomposition : la SAV est en
  // attente de décomposition côté BO. On finalise le brouillon pandora (archivage +
  // création de la SAV + note) puis on ferme le formulaire. Pas de confirmation BO ici.
  const handleDecompositionResolved = async () => {
    if (finalizingRef.current) return;
    finalizingRef.current = true;
    try {
      const result = await finalizeSavDecomposition(savDraft?._id);
      if (!result) {
        throw new Error('Échec de la finalisation du brouillon SAV');
      }
      setFormData((prev) => ({
        ...prev,
        isDraftSet: false,
        activeSavDraftId: null,
      }));
      mutateTicket(undefined, { revalidate: true });
      savDraftMutate(undefined, { revalidate: true });
      revalidateSavOrders();
      resetFormAndErrors({ setFormData, setFormError, orderDetails, user });
      dispatch(
        setSnackBar({
          open: true,
          message:
            'Commande SAV en attente de décomposition : elle sera confirmée automatiquement à la réception.',
          color: 'success',
        })
      );
      handleShowComponent(null);
    } catch (error) {
      finalizingRef.current = false;
      console.error('Finalisation décomposition:', error);
      dispatch(
        setSnackBar({
          open: true,
          message:
            "La réservation/décomposition est enregistrée, mais la finalisation du brouillon a échoué. Rechargez le ticket.",
          color: 'warning',
        })
      );
    }
  };

  const handleConfirm = async () => {
    // Si une décomposition/réservation a été appliquée, la SAV est déjà en attente de
    // décomposition côté BO : la re-modifier (PATCH) puis la confirmer échouerait.
    if (decompositionApplied) {
      dispatch(
        setSnackBar({
          open: true,
          message:
            "La commande SAV est en attente de décomposition : elle sera confirmée automatiquement à la réception.",
          color: 'info',
        })
      );
      setIsConfirmOpen(false);
      return;
    }
    try {
      const saved = await handleSavDraft();
      // L'enregistrement a échoué (ex. SAV déjà en cours pour la commande) : le
      // message explicite est déjà affiché par handleSavDraft/handleErrors, on
      // n'enchaîne donc pas la confirmation (qui écraserait ce message).
      if (!saved) {
        return;
      }
      setConfirmLoading(true);
      const confirmedSavOrder = await confirmSavDraft(savDraft._id, formData);
      // Le refus métier (ex: rupture de stock) est renvoyé par confirmSavDraft
      // sous forme de `{ errorInfos }` (voir useConfirmSavDraft) : on affiche le
      // motif réel via handleErrors et on interrompt la chaîne, comme pour
      // handleSavDraft/handleCreateOrUpdateSavOrder.
      if (handleErrors(confirmedSavOrder)) {
        return;
      }
      if (!confirmedSavOrder) {
        throw new Error('Erreur lors de la confirmation de la commande SAV');
      }
      // Forcer la réinitialisation du state
      setFormData((prev) => ({
        ...prev,
        isDraftSet: false,
        activeSavDraftId: null,
      }));
      // Mettre à jour les données
      mutateTicket(undefined, { revalidate: true });
      savDraftMutate(undefined, { revalidate: true });
      revalidateSavOrders();
      resetFormAndErrors({ setFormData, setFormError, orderDetails, user });
      const messageError = `La commande SAV est crée et confirmée.`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'success' })
      );
      handleShowComponent(null);
      return;
    } catch {
      const messageError = `La commande SAV n'as pas pu être confirmée, vérifiez les pièces et le transporteur.`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'error' })
      );
    } finally {
      setConfirmLoading(false);
      setIsConfirmOpen(false);
    }
  };

  // MISE À JOUR DU BROUILLON AVEC LES LIGNES DE LA COMMANDE SAV
  const handleCreateOrUpdateSavOrder = async (newLines) => {
    setLoading(true);
    try {
      handleFormChange({
        key: 'lines',
        value: newLines.map((line) => ({
          ...line,
          isLoading: true,
        })),
      });

      const data = await createOrUpdateSavOrder(savDraft._id, {
        ...formData,
        lines: newLines,
        isSaved: false,
      });

      handleErrors(data);

      if (data) {
        handleFormChange({
          key: 'lines',
          value: data?.lines,
        });
      }

      savDraftMutate();
      mutateTicket();
      setLoading(false);
    } catch (error) {
      console.error(
        'Erreur lors de la création ou mise à jour de la commande SAV:',
        error
      );
      setLoading(false);
    } finally {
      savDraftMutate();
      mutateTicket();
    }
  };

  const onOrderPickerChange = async (e) => {
    // TODO: handle order picker change
    // setOrderPickerLoading(true);
    handleFormChange({ key: 'orderPicker', value: e.target.value });
    // await new Promise((resolve) => setTimeout(resolve, 1000));
    // await updateSAVDraftOrderPicker(savDraft._id, {
    //   lines: formData.lines,
    //   orderPicker: e.target.value,
    // });
    // setOrderPickerLoading(false);
  };

  // ENREGISTREMENT DU BROUILLON SAV
  // Retourne `true` si le brouillon a bien été sauvegardé côté BO, `false` sinon
  // (saisie invalide ou erreur BO comme une SAV déjà existante).
  const handleSavDraft = async () => {
    setLoading(true);
    try {
      if (!validateSavFields(formData, setFormError)) {
        const messageError = `Erreur de saisie.`;
        dispatch(
          setSnackBar({ open: true, message: messageError, color: 'error' })
        );

        return false;
      }
      if (!savDraft) {
        return false;
      }

      const savDraftUpdated = await createOrUpdateSavOrder(savDraft._id, {
        ...formData,
        isSaved: true,
      });

      const hasError = handleErrors(savDraftUpdated);

      savDraftMutate();
      mutateTicket();

      if (savDraftUpdated) {
        handleFormChange({
          key: 'lines',
          value: savDraftUpdated?.lines,
        });
      }

      // Le BO a rejeté l'enregistrement (ex. SAV déjà en cours) : on n'affiche
      // pas de faux succès et on signale l'échec à l'appelant.
      if (hasError) {
        return false;
      }

      const messageError = `Brouillon sauvegardé.`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'success' })
      );
      return true;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du brouillon:', error);
      const messageError = `Le brouillon n'as pas pu être sauvegardé.`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'error' })
      );
      return false;
    } finally {
      savDraftMutate();
      mutateTicket();
      setLoading(false);
    }
  };

  const handleSaveAndDecompose = async () => {
    setLoading(true);
    try {
      if (!validateSavFields(formData, setFormError)) {
        dispatch(
          setSnackBar({
            open: true,
            message: 'Erreur de saisie.',
            color: 'error',
          })
        );
        return;
      }
      if (!savDraft) return;

      const savDraftUpdated = await createOrUpdateSavOrder(savDraft._id, {
        ...formData,
        isSaved: true,
      });

      const hasError = handleErrors(savDraftUpdated);

      if (savDraftUpdated) {
        handleFormChange({ key: 'lines', value: savDraftUpdated?.lines });
      }

      // Le BO a rejeté la sauvegarde (ex. SAV déjà en cours) : on n'enchaîne pas
      // la décomposition et on n'affiche pas de message de succès trompeur.
      if (hasError) {
        return;
      }

      await decompositionRef.current?.createAllPending();

      dispatch(
        setSnackBar({
          open: true,
          message: 'Brouillon sauvegardé et décomposition créée.',
          color: 'success',
        })
      );
    } catch (error) {
      console.error('Erreur lors de la sauvegarde et décomposition:', error);
      dispatch(
        setSnackBar({
          open: true,
          message:
            error.message || 'Erreur lors de la création de la décomposition.',
          color: 'error',
        })
      );
    } finally {
      savDraftMutate();
      mutateTicket();
      setLoading(false);
    }
  };

  const handleOpenConfirmDelete = () => {
    setIsConfirmDeleteOpen(true);
  };

  // SUPPRESSION DU BROUILLON SAV ET ANNULATION DE LA COMMANDE SAV
  const handleConfirmDelete = async () => {
    setConfirmLoading(true);
    try {
      if (!savDraft?._id) {
        throw new Error('No active draft found');
      }
      const deletedDraft = await deleteDraft(savDraft._id);
      if (!deletedDraft) {
        throw new Error(`delete draft failed ${savDraft._id}`);
      }
      // Forcer la réinitialisation du state
      setFormData((prev) => ({
        ...prev,
        isDraftSet: false,
        activeSavDraftId: null,
      }));
      // Mettre à jour les données
      mutateTicket(undefined, { revalidate: true });
      savDraftMutate(undefined, { revalidate: true });
      revalidateSavOrders();
      resetFormAndErrors({ setFormData, setFormError, orderDetails, user });
      const messageError = `Le brouillon a été supprimé.`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'success' })
      );

      handleShowComponent(null);
    } catch (error) {
      console.error('Error deleting draft:', error);
      const messageError = `Le brouillon n'as pas pu être supprimé.`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'error' })
      );
    } finally {
      setConfirmLoading(false);
      setIsConfirmDeleteOpen(false);
    }
  };

  useEffect(() => {
    if (activeSavDraftId && activeSavDraftId !== formData.activeSavDraftId) {
      setFormData((prev) => ({
        ...prev,
        isDraftSet: false,
        activeSavDraftId: activeSavDraftId,
      }));
    }
  }, [activeSavDraftId]);

  useEffect(() => {
    if (savDraft?._id && !formData.activeSavDraftId) {
      setFormData((prev) => ({
        ...prev,
        activeSavDraftId: savDraft._id,
      }));
    }
  }, [savDraft?._id]);

  useEffect(() => {
    if (isUsingShippingAddress) {
      setFormData((prevData) => ({
        ...prevData,
        billingAddress: formData.shippingAddress,
      }));
    }
  }, [formData.shippingAddress]);

  useEffect(() => {
    if (
      !formData.isSet &&
      orderDetails?.addresses?.shipping &&
      orderDetails?.addresses?.billing
    ) {
      const initShipping = addressFormat('shipping');
      const initBilling = addressFormat('billing');

      setFormData((prev) => ({
        ...prev,
        shippingAddress: initShipping,
        billingAddress: initBilling,
        isSet: true,
      }));
    }
  }, [orderDetails, formData.isSet]);

  useEffect(() => {
    if (savDraftLoading || savDraftValidating) {
      return;
    }
    if (!savDraft?._id) {
      return;
    }

    const effectiveDraftId = activeSavDraftId || savDraft._id;

    if (!formData.isDraftSet) {
      setFormData((prev) => ({
        ...prev,
        isDraftSet: true,
        activeSavDraftId: effectiveDraftId,
        savDraftReference: savDraft?.reference,
        savOrder: savDraft?.savOrder || '',
        reason: savDraft.reason?.name || '',
        customerOrder: savDraft.customerOrder,
        amountTtcNet: savDraft.amount || 100,
        phone:
          savDraft?.phone ||
          prev?.phone ||
          orderDetails?.addresses?.shipping?.phone ||
          '',
        firstName: savDraft?.firstName || prev?.firstName || '',
        lastName: savDraft?.lastName || prev?.lastName || '',
        lines: (savDraft.lines || []).map((line) => ({
          id: line._id,
          internalProduct: line.internalProduct,
          reference: line.reference,
          quantity: line.quantity,
          isAllocated: line.isAllocated,
          isValid: line.isValid,
          title: line.title,
          stockQuantity:
            typeof line.stockQuantity === 'number' ? line.stockQuantity : null,
        })),
        shippingAddress: {
          address1: savDraft.shippingAddress?.line1 || '',
          address2: savDraft.shippingAddress?.line2 || '',
          postalCode: savDraft.shippingAddress?.zipCode || '',
          city: savDraft.shippingAddress?.city || '',
          ...prev.shippingAddress,
        },

        billingAddress: {
          address1: savDraft.billingAddress?.line1 || '',
          address2: savDraft.billingAddress?.line2 || '',
          postalCode: savDraft.billingAddress?.zipCode || '',
          city: savDraft.billingAddress?.city || '',
          ...prev.billingAddress,
        },
        desiredShippingDate: savDraft.desiredShippingDate || null,
        isPayable: savDraft.isPayable || false,
        orderPicker: savDraft?.orderPicker || 'Yusen',
        paymentMethod: savDraft?.paymentMethod || '',
        createdBy: savDraft?.createdBy?.email || user?.email || '',
        currency: savDraft?.currency?.code || orderDetails?.currency?.code,
        comment: savDraft?.comment || '',
        updatedAt: savDraft?.updatedAt || '',
      }));
    }
  }, [
    orderDetails,
    savDraft?._id,
    savDraftLoading,
    savDraftValidating,
    activeSavDraftId,
    formData.isDraftSet,
  ]);

  useEffect(() => {
    if (savDraft?.savOrder && !formData.savOrder) {
      setFormData((prev) => ({ ...prev, savOrder: savDraft.savOrder }));
    }
  }, [savDraft?.savOrder]);

  // Rafraîchit une seule fois le niveau de stock des lignes valides dont le
  // stockQuantity n'a pas encore été récupéré (brouillons créés avant l'ajout
  // de la pastille de stock). Réutilise le flux existant de mise à jour.
  useEffect(() => {
    if (stockRefreshDoneRef.current) return;
    if (!formData.isDraftSet || !savDraft?._id || loading) return;
    const needsStockRefresh = formData.lines?.some(
      (line) => line.isValid !== false && typeof line.stockQuantity !== 'number'
    );
    if (needsStockRefresh) {
      stockRefreshDoneRef.current = true;
      handleCreateOrUpdateSavOrder(formData.lines);
    }
  }, [formData.isDraftSet, formData.lines, savDraft?._id, loading]);

  // Synchroniser displayAmount avec les changements du formData (changements externes)
  useEffect(() => {
    if (formData.amountTtcNet !== undefined && formData.amountTtcNet !== null) {
      const formattedAmount = formData.amountTtcNet
        .toString()
        .replace('.', ',');
      setDisplayAmount(formattedAmount);
    }
  }, [formData.amountTtcNet]);

  // Nettoyage du timeout lors du démontage du composant
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Fonction de nettoyage et validation de la valeur
  const cleanAmountValue = useCallback((value) => {
    // Filtrer les caractères autorisés : chiffres, point et virgule uniquement
    const sanitizedValue = value.replace(/[^0-9.,]/g, '');

    // Remplacer les virgules par des points pour la logique interne
    const normalizedValue = sanitizedValue.replace(',', '.');

    // Éviter les multiples points consécutifs
    const cleanValue = normalizedValue.replace(/\.{2,}/g, '.');

    // S'assurer qu'il n'y a qu'un seul point décimal
    const parts = cleanValue.split('.');
    const finalValue =
      parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : cleanValue;

    return finalValue;
  }, []);

  // Fonction debounce pour la mise à jour du formData
  const debouncedUpdateAmount = useCallback(
    (value) => {
      // Clear le timeout précédent
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Créer un nouveau timeout
      debounceRef.current = setTimeout(() => {
        const numericValue = parseFloat(value) || 0;
        handleFormChange({
          key: 'amountTtcNet',
          value: numericValue,
        });
      }, 300); // Debounce de 300ms
    },
    [handleFormChange]
  );

  const handleAmountChange = (e) => {
    const value = e.target.value;

    // Nettoyer la valeur
    const cleanedValue = cleanAmountValue(value);

    // Mettre à jour l'affichage immédiatement (avec virgule pour l'UX française)
    const displayValue = cleanedValue.replace('.', ',');
    setDisplayAmount(displayValue);

    // Mettre à jour le formData avec debounce
    if (cleanedValue) {
      debouncedUpdateAmount(cleanedValue);
    }
  };

  const isSAVPayable = useMemo(() => {
    const isMP = orderDetails?.isMP ? true : false;
    const isSicaan = orderDetails?.contact?.store?.brand?.name === 'Sicaan';
    return isSicaan || isMP;
  }, [orderDetails]);

  if (!formData.isDraftSet || !formData.activeSavDraftId) {
    return (
      <>
        <SavOrderHeader handleShowComponent={() => handleShowComponent(null)} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '55vh',
          }}
        >
          <Typography>Chargement du brouillon...</Typography>
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <SavOrderHeader handleShowComponent={() => handleShowComponent(null)} />
      <Card
        elevation={5}
        sx={{
          width: '100%',
          padding: 2,
          gap: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {formData?.savDraftReference && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Typography
              color="primary"
              sx={{ fontSize: '1em', fontWeight: 'bold' }}
            >
              Brouillon de commande SAV - {formData?.savDraftReference}
            </Typography>
          </Box>
        )}
        {orderDetails?.amounts?.discount?.code && (
          <Alert variant="outlined" color="warning" icon={<LocalOfferIcon />}>
            La commande à une réduction appliquée:{' '}
            {orderDetails?.amounts?.discount?.code} -{' '}
            {orderDetails?.amounts?.discount?.value} {' | '}
            {`${orderDetails?.amounts?.discount?.amount}${getSymbolFromCurrency(orderDetails?.currency?.code)}`}
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Référence de la commande"
            value={formData.customerOrder}
            error={Boolean(formError.customerOrder)}
            helperText={formError.customerOrder}
            disabled
            fullWidth
            size="small"
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mt: 2,
            mb: 2,
          }}
        >
          <Typography
            color="primary"
            sx={{ fontSize: '1em', fontWeight: 'bold' }}
          >
            Type de commande SAV
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, position: 'relative' }}>
            <Button
              onClick={() =>
                handleFormChange({ key: 'isPayable', value: false })
              }
              variant={formData.isPayable ? 'outlined' : 'contained'}
              size="small"
              sx={{ fontSize: '0.7em', padding: '0 0.5em' }}
            >
              Gratuite
            </Button>
            <Button
              disabled={isSAVPayable}
              onClick={() =>
                handleFormChange({ key: 'isPayable', value: true })
              }
              variant={formData.isPayable ? 'contained' : 'outlined'}
              size="small"
              sx={{ fontSize: '0.7em', padding: '0 0.5em' }}
            >
              Payante
            </Button>
            {delayOverDue && (
              <Typography
                sx={{
                  position: 'absolute',
                  bottom: -20,
                  left: 0,
                  fontSize: '0.7em',
                  color: 'warning.main',
                }}
              >
                Le délai de prise en charge gratuit est dépassé
              </Typography>
            )}
          </Box>
        </Box>
        <Tooltip
          placement="top"
          title={`
            1. Vous pouvez ajouter des PCS générique et les faire partir du
            bureau avec cette référence: PCS-001434. 
            2. Le statut de stock des pièces est lié au préparateur. Ex: Des
            ACC-000143 ne pourront pas partir de Yussen
            `}
        >
          <Typography
            color="primary"
            sx={{ fontSize: '1em', fontWeight: 'bold', mb: 1, cursor: 'help' }}
          >
            Liste des produits et préparateur{' '}
            <InfoOutlinedIcon sx={{ fontSize: '0.9em' }} />
          </Typography>
        </Tooltip>
        <FormControl
          fullWidth
          size="small"
          error={Boolean(formError.orderPicker)}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <InputLabel>Préparateur</InputLabel>
          <Select
            // TODO
            // disabled={orderPickerLoading}
            value={formData.orderPicker}
            onChange={onOrderPickerChange}
            label="Préparateur"
            size="small"
            sx={{ width: '200px' }}
          >
            <MenuItem value="Yusen">Yusen</MenuItem>
            <MenuItem value="Bureau">Bureau</MenuItem>
          </Select>
          {/* {orderPickerLoading && <CircularProgress size={20} />} */}
        </FormControl>
        <SavLinesForm
          formData={formData}
          handleFormChange={handleFormChange}
          formError={formError}
          handleCreateOrUpdateSavOrder={handleCreateOrUpdateSavOrder}
          disabled={decompositionApplied}
        />

        {hasDecompositionFlow && (
          <SavDecompositionCheck
            ref={decompositionRef}
            lines={formData.lines}
            origin={formData.savOrder}
            createdBy={formData.createdBy}
            onCheckDone={({ checked = false, requiresAction = false } = {}) => {
              setHasCheckedDecomposition(checked);
              setDecompositionCheckRequiresAction(checked && requiresAction);
            }}
            onDecompositionApplied={() => setDecompositionApplied(true)}
            onAllResolved={handleDecompositionResolved}
            locked={decompositionApplied}
          />
        )}

        <Divider />

        {formData?.isPayable && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl size="small" sx={{ width: '200px' }}>
              <InputLabel htmlFor="outlined-adornment-amount">
                Montant TTC
              </InputLabel>
              <Input
                id="amount-input"
                placeholder="0,00"
                value={displayAmount}
                onChange={handleAmountChange}
                error={Boolean(formError.amountTtcNet)}
                inputProps={{
                  inputMode: 'decimal',
                  'aria-label': 'Montant TTC',
                }}
                endAdornment={
                  <InputAdornment position="end">
                    {getSymbolFromCurrency(formData.currency)}
                  </InputAdornment>
                }
              />
              {/* <Input
                // id="outlined-adornment-amount"
                value={formatAmountForDisplay(formData.amountTtcNet)}
                onChange={handleAmountChange}
                error={Boolean(formError.amountTtcNet)}
                endAdornment={
                  <InputAdornment position="end">
                    {getSymbolFromCurrency(formData.currency)}
                  </InputAdornment>
                }
                label="Montant TTC"
                placeholder="0,00"
                // inputProps={{
                //   inputMode: 'decimal',
                // }}
              /> */}
              {formError.amountTtcNet && (
                <FormHelperText error>{formError.amountTtcNet}</FormHelperText>
              )}
            </FormControl>
            {formData.currency !== 'EUR' && (
              <Typography sx={{ fontSize: '0.8em', color: 'warning.main' }}>
                La devise de la commande n&apos;est pas en euro: vérifiez le
                montant.
              </Typography>
            )}
          </Box>
        )}

        <FormControl fullWidth size="small" error={Boolean(formError.reason)}>
          <InputLabel>Motif de commande SAV</InputLabel>
          <Select
            value={formData?.reason}
            label="Motif de commande SAV"
            onChange={(e) =>
              handleFormChange({ key: 'reason', value: e.target.value })
            }
            sx={{ width: '50%' }}
          >
            <MenuItem key={'payant'} value={null} disabled>
              Payant
            </MenuItem>
            {savOrderReasons
              ?.filter((reason) => reason?.isPayable)
              ?.map((reason) => (
                <MenuItem key={reason?._id} value={reason?.name}>
                  {reason?.name}
                </MenuItem>
              ))}
            <MenuItem key={'gratuit'} value={null} disabled>
              Gratuit
            </MenuItem>
            {savOrderReasons
              ?.filter((reason) => !reason?.isPayable)
              ?.map((reason) => (
                <MenuItem key={reason?._id} value={reason?.name}>
                  {reason?.name}
                </MenuItem>
              ))}
          </Select>
          {formError.reason && (
            <FormHelperText>{formError.reason}</FormHelperText>
          )}
        </FormControl>

        <CustomerInfos
          formData={formData}
          handleFormChange={handleFormChange}
          formError={formError}
        />
        <SetAddress
          formData={formData}
          orderDetails={orderDetails}
          handleFormChange={handleFormChange}
          formError={formError}
          addressName="shipping"
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={isUsingShippingAddress}
                onChange={(e) => handleSwitchChange(e.target.checked)}
              />
            }
            label="Adresse de facturation identique à la livraison"
          />
        </Box>
        {!isUsingShippingAddress ? (
          <SetAddress
            formData={formData}
            orderDetails={orderDetails}
            handleFormChange={handleFormChange}
            formError={formError}
            addressName="billing"
          />
        ) : null}

        <SavDateTime
          formData={formData}
          handleFormChange={handleFormChange}
          formError={formError}
        />

        <FormControl
          error={Boolean(formError.comment)}
          fullWidth
          sx={{ mt: 2 }}
        >
          <Typography sx={{ fontSize: '0.8em' }}>Commentaire</Typography>
          <TextField
            multiline
            rows={4}
            value={formData.comment}
            onChange={(e) =>
              handleFormChange({ key: 'comment', value: e.target.value })
            }
            placeholder="Ajoutez des informations sur la commande SAV."
            error={Boolean(formError.comment)}
            helperText={
              formError.comment || 'Texte à destination des opérateurs internes'
            }
          />
        </FormControl>

        {formData?.updatedAt && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Typography
              color="primary"
              sx={{ fontSize: '0.8em', fontWeight: 'bold' }}
            >
              Dernière mise à jour -{' '}
              {formData.updatedAt
                ? format(
                    new Date(formData.updatedAt),
                    "dd MMMM yyyy 'à' HH:mm",
                    {
                      locale: fr,
                    }
                  )
                : 'Aucune mise à jour'}
            </Typography>
          </Box>
        )}

        {decompositionApplied && (
          <Alert severity="info" sx={{ mt: 2 }}>
            La commande SAV est en attente de décomposition. Elle sera confirmée
            automatiquement à la réception de la décomposition : aucune
            confirmation manuelle n&apos;est nécessaire.
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            variant="outlined"
            color="info"
            onClick={() => handleShowComponent(null)}
          >
            Annuler
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              endIcon={<DeleteIcon />}
              variant="contained"
              color="error"
              onClick={() => handleOpenConfirmDelete()}
            >
              Supprimer
            </Button>
            {hasDecompositionFlow && (
              <Button
                endIcon={
                  loading ? <CircularProgress size={16} /> : <BuildIcon />
                }
                disabled={
                  loading || !hasCheckedDecomposition || decompositionApplied
                }
                onClick={handleSaveAndDecompose}
                variant="outlined"
                color="warning"
              >
                Créer la décomposition et sauvegarder
              </Button>
            )}
            <Button
              endIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
              disabled={loading || decompositionApplied}
              onClick={() => handleSavDraft()}
              variant="outlined"
              color="info"
            >
              Sauvegarder
            </Button>
            <Button
              endIcon={<CheckIcon />}
              variant="contained"
              color="primary"
              onClick={handleOpenConfirm}
              disabled={decompositionApplied || isConfirmLockedByDecomposition}
            >
              Confirmer
            </Button>
          </Box>
        </Box>
      </Card>
      <SavConfirmModal
        isOpen={isConfirmOpen}
        handleClose={() => setIsConfirmOpen(false)}
        handleConfirm={handleConfirm}
        formData={formData}
        confirmLoading={confirmLoading}
      />
      <SavDraftDeleteModal
        isOpen={isConfirmDeleteOpen}
        handleClose={() => setIsConfirmDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        draftReference={formData.savDraftReference}
        confirmLoading={confirmLoading}
      />
    </>
  );
};

export default SavOrderForm;
